import React, { useCallback } from "react";
import { QueryKey } from "@tanstack/react-query";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { CreateLeadRequest_DocumentFindLeadsMetadata } from "lib/generated/app-service-gql/graphql";
import {
  CreateLeadFormOutput,
  createLeadSchema,
} from "../../../../components/leads/create_lead/utils";
import { routePaths } from "../../../routes";
import { EventData } from "../../../tracking";
import { useCreateLead } from "./useCreateLead";
import { useDefaultLeadWorkflowStage } from "./useDefaultLeadWorkflowStage";

// Optional payload the FE attaches when creating a lead from a document find-leads card.
// The server stamps a `lead.metadata` blob (with a server-computed dedupeKey) so the
// find-leads merge step can later mark cached AI proposals as "already a lead".
// `source` is an FE-only discriminator that's stripped before sending to GraphQL.
export type DocumentFindLeadsMetadata = CreateLeadRequest_DocumentFindLeadsMetadata & {
  source: "document_find_leads";
};

// Maps the FE metadata onto the nested `documentFindLeadsMetadata` field accepted by
// the `createLead` mutation. Returns an empty object when no metadata is provided so
// callers can spread it unconditionally. The FE-only `source` discriminator is not forwarded.
export function documentFindLeadsMetadataToLeadFields(metadata?: DocumentFindLeadsMetadata) {
  if (!metadata) return {};
  return {
    documentFindLeadsMetadata: {
      leadType: metadata.leadType,
      ...(metadata.motivation !== undefined && { motivation: metadata.motivation }),
      ...(metadata.matchingText !== undefined && { matchingText: metadata.matchingText }),
      ...(metadata.pageNumbers !== undefined && { pageNumbers: metadata.pageNumbers }),
      ...(metadata.locations && {
        locations: metadata.locations.map((loc) => ({
          pageNumber: loc.pageNumber,
          ...(loc.bbox && { bbox: loc.bbox }),
        })),
      }),
    },
  };
}

type Props = {
  trackingData: EventData;
  defaultValues: Partial<CreateLeadFormOutput>;
  metadata?: DocumentFindLeadsMetadata;
  mutationKey?: QueryKey;
  onCreateLead?: (leadId: string) => void;
  onSettled?: () => void;
};

type CreateLeadOneClick = {
  isLoading: boolean;
  createLeadFromDefaultValues: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export function useCreateLeadOneClick({
  trackingData,
  defaultValues,
  metadata,
  mutationKey,
  onCreateLead,
  onSettled,
}: Props): CreateLeadOneClick {
  const { defaultStageId, isLoading: isLoadingDefaultStage } =
    useDefaultLeadWorkflowStage(defaultValues);

  const { mutate: createLead, isLoading: isCreatingLead } = useCreateLead(trackingData, {
    mutationKey,
    onSuccess: (data) => {
      toast.success(`Lead "${data.createLead.name}" created`, {
        action: (
          <Button as="a" to={routePaths.leadDetail} params={{ id: data.createLead.id }}>
            View
          </Button>
        ),
      });
      onCreateLead?.(data.createLead.id);
    },
    onError: () => {
      toast.error("Failed to create lead");
    },
    onSettled: () => {
      onSettled?.();
    },
  });

  const createLeadFromDefaultValues = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();

      if (!defaultStageId) {
        toast.error("Could not determine default stage for lead");
        return;
      }

      const { entities, ...lead } = createLeadSchema.parse({
        ...defaultValues,
        stageId: defaultValues.stageId ?? defaultStageId,
      });

      createLead({
        lead: {
          ...lead,
          ...documentFindLeadsMetadataToLeadFields(metadata),
        },
        entities: { entities: entities ?? [] },
      });
    },
    [createLead, defaultStageId, defaultValues, metadata],
  );

  return {
    isLoading: isCreatingLead || isLoadingDefaultStage,
    createLeadFromDefaultValues,
  };
}
